import {
  deleteInvitation,
  Invitation,
  postUserInvitation,
  putResendInvitation,
} from '@back-office/http';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { EmailResendWaitTimeMap, PH_ISD_CODE } from '@back-office/ui';
import { joiResolver } from '@hookform/resolvers/joi';
import { inRange } from 'lodash';
import { Instance } from 'mobx-state-tree';
import { useSnackbar } from 'notistack';
import { BaseSyntheticEvent, useState } from 'react';
import {
  Control,
  DeepMap,
  FieldError,
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  UnpackNestedValue,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMount, useUnmount } from 'react-use';
import { BUTTON_CLICK_WAIT, STATUS_CODE_SUCCESS } from '../../constants';
import {
  emailSchema,
  emailValidator,
  InviteNewUserFieldNames,
  InviteNewUserForm,
} from '../../forms';
import useInject from '../../hooks/useInject';
import AlertModel from '../../stores/alert.store';
import InvitationModel from '../../stores/invitations.store';

type HandleSubmitFn = <
  TSubmitFieldValues extends FieldValues = InviteNewUserForm
>(
  onValid: SubmitHandler<TSubmitFieldValues>,
  onInvalid?: SubmitErrorHandler<InviteNewUserForm> | undefined
) => (e?: BaseSyntheticEvent) => Promise<void>;

type GetValueType = {
  (): UnpackNestedValue<InviteNewUserForm>;
  <TFieldName extends string, TFieldValue>(
    name: TFieldName
  ): TFieldName extends keyof InviteNewUserForm
    ? UnpackNestedValue<InviteNewUserForm>[TFieldName]
    : TFieldValue;
  <TFieldName extends keyof InviteNewUserForm>(
    names: TFieldName[]
  ): UnpackNestedValue<Pick<InviteNewUserForm, TFieldName>>;
};

type SetValueFn = (
  name: keyof InviteNewUserForm,
  value: unknown,
  config?:
    | Partial<{
        shouldValidate: boolean;
        shouldDirty: boolean;
      }>
    | undefined
) => void;

type ResetFn = (
  values?:
    | {
        email?: string | undefined;
        phone?: string | undefined;
      }
    | undefined,
  omitResetState?:
    | Partial<{
        errors: boolean;
        isDirty: boolean;
        isSubmitted: boolean;
        touched: boolean;
        isValid: boolean;
        submitCount: boolean;
        dirtyFields: boolean;
      }>
    | undefined
) => void;

type UseViewModelReturn = {
  control: Control<InviteNewUserForm>;
  handleSubmit: HandleSubmitFn;
  errors: DeepMap<InviteNewUserForm, FieldError>;
  clearErrors: (
    name?: keyof InviteNewUserForm | (keyof InviteNewUserForm)[] | undefined
  ) => void;
  getValues: GetValueType;
  setValue: SetValueFn;
  reset: ResetFn;
  invitations: Instance<typeof InvitationModel>;
  alert: Instance<typeof AlertModel>;
  isPhoneNumberExist?: boolean;
  showSuccessMessage?: boolean;
  emailToTimerMap: EmailResendWaitTimeMap;
  handleOnSubmit: (form: InviteNewUserForm) => Promise<void>;
  validateForm: () => void;
  handleSearchPhoneNumber: () => Promise<void>;
  openResendAlert: (invitation: Invitation) => void;
  openRemoveAlert: (invitation: Invitation) => void;
  enableSubmitButton: boolean;
};

const useViewModel = (): UseViewModelReturn => {
  const {
    control,
    handleSubmit,
    errors,
    clearErrors,
    getValues,
    setValue,
    reset,
  } = useForm<InviteNewUserForm>({
    mode: 'onSubmit',
    resolver: joiResolver(emailSchema),
  });
  const { getInvitationList, invitations, auth, alert, getPhoneNumber } =
    useInject((store) => store);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [enableSubmitButton, setEnableSubmitButton] = useState<boolean>(false);
  const [isPhoneNumberExist, setIsPhoneNumberExist] = useState<boolean>();
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>();
  const [emailToTimerMap, setEmailToTimerMap] =
    useState<EmailResendWaitTimeMap>({});

  useMount(() => {
    getInvitationList();
  });

  useUnmount(() => {
    invitations.reset();
  });

  const openSnackbar = async (
    isSuccess: boolean,
    message: string
  ): Promise<void> => {
    if (isSuccess) {
      enqueueSnackbar(t(message), {
        variant: 'success',
      });
      await getInvitationList();
    } else {
      enqueueSnackbar(t(message), {
        variant: 'error',
      });
      await getInvitationList();
    }
  };

  const resend = async (invitation: Invitation): Promise<void> => {
    await putResendInvitation({
      email: invitation.email,
      mid: invitation.mid,
      phoneNo: invitation.phone || '',
    }).then(({ data: { status } }) => {
      status.code === STATUS_CODE_SUCCESS
        ? openSnackbar(true, 'dialog.success_resend_invite')
        : openSnackbar(false, 'dialog.failed_resend_invite');
    });

    const timeOfEmail = emailToTimerMap[invitation.email];
    const isTimeOfEmailInRange = inRange(
      timeOfEmail,
      BUTTON_CLICK_WAIT,
      BUTTON_CLICK_WAIT * 2
    );
    const newTimeOfEmail = isTimeOfEmailInRange
      ? timeOfEmail + 5
      : BUTTON_CLICK_WAIT;
    setEmailToTimerMap({
      ...emailToTimerMap,
      [invitation.email]: newTimeOfEmail,
    });
  };

  const remove = async (invitation: Invitation): Promise<void> => {
    await deleteInvitation(invitation.email)
      .then(({ data: { status } }) => {
        status.code === STATUS_CODE_SUCCESS
          ? openSnackbar(true, 'invite_new_user.success_delete_user')
          : openSnackbar(false, 'invite_new_user.failed_delete_user');
        alert.closeDialog();
      })
      .finally(() => alert.closeDialog());
  };

  const openResendAlert = (invitation: Invitation): void => {
    alert.openAlert({
      cancelBtn: 'dialog.cancel',
      message: 'dialog.resend_invite',
      okBtn: 'dialog.confirm',
      title: 'dialog.confirmation',
      handleOK() {
        resend(invitation);
        alert.closeDialog();
      },
      handleCancel() {
        alert.closeDialog();
      },
    });
  };

  const openRemoveAlert = (invitation: Invitation): void => {
    alert.openAlert({
      cancelBtn: 'dialog.cancel',
      message: 'dialog.cancel_invite',
      okBtn: 'dialog.confirm',
      title: 'dialog.confirmation',
      handleOK() {
        remove(invitation);
        alert.closeDialog();
      },
      handleCancel() {
        alert.closeDialog();
      },
    });
  };

  const handleSendUserInvitation = async (
    email: string,
    phoneNumber: string
  ): Promise<void> => {
    await postUserInvitation({
      email,
      mid: auth.currentUser.mid,
      phoneNo: phoneNumber,
    })
      .then(({ data: { status } }) => {
        setIsPhoneNumberExist(undefined);
        setShowSuccessMessage(status.code === STATUS_CODE_SUCCESS);
        getInvitationList();
      })
      .finally(() => {
        const timeoutID = setInterval(() => {
          clearInterval(timeoutID);
          reset();
          setShowSuccessMessage(undefined);
        }, 5000);
        alert.closeDialog();
      });
  };

  const handleOnSubmit = async (form: InviteNewUserForm): Promise<void> => {
    const email = form.email.toLowerCase();
    const phoneNumber = `${PH_ISD_CODE}${form.phone}`;

    alert.openAlert({
      title: t('invite_new_user.popup_title'),
      cancelBtn: 'dialog.cancel',
      okBtn: 'dialog.confirm',
      message: t('invite_new_user.popup_content', {
        days: 7,
      }),
      handleOK: async () => {
        await handleSendUserInvitation(email, phoneNumber);
      },
      handleCancel: () => {
        alert.closeDialog();
      },
    });
  };

  const validateForm = (): void => {
    emailSchema
      .validateAsync(getValues())
      .then(() => setEnableSubmitButton(true))
      .catch(() => setEnableSubmitButton(false));
    if (!getValues().email) {
      setIsPhoneNumberExist(undefined);
    }
  };

  const handleFillPhoneNumberField = async (): Promise<void> => {
    try {
      const email = getValues().email.toLowerCase();
      const phoneNumber = await getPhoneNumber(email);
      if (!phoneNumber) {
        throw new Error('Phone number for this email not found');
      }
      setValue(InviteNewUserFieldNames.Phone, phoneNumber);
      setIsPhoneNumberExist(true);
    } catch {
      setValue(InviteNewUserFieldNames.Phone, '');
      setIsPhoneNumberExist(false);
    } finally {
      validateForm();
    }
  };

  const handleValidateEmailField = async (): Promise<void> => {
    try {
      const email = getValues().email.toLowerCase();
      await emailValidator.validateAsync(email);
    } catch {
      setEnableSubmitButton(false);
    }
  };

  const handleSearchPhoneNumber = async (): Promise<void> => {
    await handleValidateEmailField();
    await handleFillPhoneNumberField();
  };

  return {
    control,
    handleSubmit,
    errors,
    clearErrors,
    getValues,
    setValue,
    reset,
    invitations,
    alert,
    isPhoneNumberExist,
    showSuccessMessage,
    emailToTimerMap,
    handleOnSubmit,
    validateForm,
    handleSearchPhoneNumber,
    openResendAlert,
    openRemoveAlert,
    enableSubmitButton,
  };
};

export default useViewModel;
